import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import get from 'lodash/get';
import { successM, logoBlueM, logoGreenM, logoYellowM, primaryM, backgroundGrayLightM } from 'web-ui-kit/colors';
import { BodySmallStyles, BodyBoldSmallStyles } from 'web-ui-kit/typography';
import CheckIcon from 'web-ui-kit/assets/CheckIcon';
import PromoIcon from 'web-ui-kit/assets/PromoIcon';

function _taggedTemplateLiteral(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }

  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArrayLimit(arr, i) {
  if (typeof Symbol === "undefined" || !(Symbol.iterator in Object(arr))) return;
  var _arr = [];
  var _n = true;
  var _d = false;
  var _e = undefined;

  try {
    for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(n);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }

  return arr2;
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

var _templateObject, _templateObject2, _templateObject3, _templateObject4, _templateObject5, _templateObject6, _templateObject7, _templateObject8, _templateObject9;
/**
 * The Tiered Promo component appears in relation to the cart, and shows
 * a customer how close they are to the next promotion based on their
 * current cart total cost.
 */


var VARIANTS = ['default', 'checkout'];

function TieredPromo(_ref) {
  var _ref$className = _ref.className,
      _ref$subtotal = _ref.subtotal,
      subtotal = _ref$subtotal === void 0 ? 0.0 : _ref$subtotal,
      _ref$variant = _ref.variant,
      variant = _ref$variant === void 0 ? 'default' : _ref$variant;
  var CongratsMsg = styles.CongratsMsg,
      CongratsMsgText = styles.CongratsMsgText,
      Container = styles.Container,
      Progress = styles.Progress,
      ProgressBar = styles.ProgressBar,
      PromoCheckIcon = styles.PromoCheckIcon,
      PromoIconProgress = styles.PromoIconProgress,
      PromoText = styles.PromoText,
      PromoTextWrapper = styles.PromoTextWrapper;

  var _useState = useState(''),
      _useState2 = _slicedToArray(_useState, 2),
      congratsCopy = _useState2[0],
      setCongratsCopy = _useState2[1];

  var _useState3 = useState(''),
      _useState4 = _slicedToArray(_useState3, 2),
      promoCopy = _useState4[0],
      setPromoCopy = _useState4[1];

  var _useState5 = useState(0),
      _useState6 = _slicedToArray(_useState5, 2),
      tierLevel = _useState6[0],
      setTierLevel = _useState6[1];

  var _useState7 = useState(tierColors[0]),
      _useState8 = _slicedToArray(_useState7, 2),
      promoColor = _useState8[0],
      setPromoColor = _useState8[1];

  var isTiered = tierLevel > 0;
  var nextGoal = get(savingsInfo, "[".concat(tierLevel, "].savingsGoal"), null);
  var nextSavings = get(savingsInfo, "[".concat(tierLevel, "].savings"), null);
  var finalGoal = savingsInfo[savingsInfo.length - 1].savingsGoal; // Progress bar percentage.

  var percentMath = subtotal / nextGoal;

  var _useState9 = useState(0),
      _useState10 = _slicedToArray(_useState9, 2),
      percentage = _useState10[0],
      setPercentage = _useState10[1];

  useEffect(function () {
    var firstStr = percentage > 0.5 ? "You're so close! Save $" : "Save $";
    var secondStr = " when you purchase ".concat(nextGoal, " or more.");
    setPromoCopy(addSpan(firstStr, secondStr, nextSavings));

    if (subtotal < finalGoal) {
      var currentTier = 0;
      savingsInfo.forEach(function (tier, index) {
        var _savingsInfo;

        subtotal >= savingsInfo[index].savingsGoal && subtotal < (((_savingsInfo = savingsInfo[index + 1]) === null || _savingsInfo === void 0 ? void 0 : _savingsInfo.savingsGoal) || Number.MAX_SAFE_INTEGER) ? currentTier = index + 1 : setTierLevel(0);
        setTierLevel(currentTier);
        setPromoColor(tierColors[tierLevel]);
        isTiered > 0 ? setCongratsCopy("Congrats! You\u2019ve saved $".concat(savingsInfo[tierLevel - 1].savings, " so far")) : setCongratsCopy('');
        setPercentage(percentMath);
      });
    } else {
      // End of tiered promos
      setPromoCopy(tierColors[tierColors.length - 1]);
      setCongratsCopy("Congrats! You\u2019ve saved $".concat(savingsInfo[savingsInfo.length - 1].savings));
      setPromoCopy('');
      setPercentage(1);
    }
  }, [subtotal, tierLevel]);
  return (
    /*#__PURE__*/
    React.createElement(Container, {
      variant: variant
    }, isTiered &&
    /*#__PURE__*/
    React.createElement(CongratsMsg, null,
    /*#__PURE__*/
    React.createElement(CongratsMsgText, null, congratsCopy),
    /*#__PURE__*/
    React.createElement(PromoCheckIcon, {
      size: 18,
      variant: "success"
    })),
    /*#__PURE__*/
    React.createElement(ProgressBar, {
      variant: variant
    },
    /*#__PURE__*/
    React.createElement(Progress, {
      value: percentage,
      role: "progressbar",
      "aria-valuenow": percentage,
      "aria-valuemin": '0',
      "aria-valuemax": '1',
      promoColor: promoColor
    })),
    /*#__PURE__*/
    React.createElement(PromoTextWrapper, null,
    /*#__PURE__*/
    React.createElement(PromoIconProgress, {
      size: 24,
      promoColor: promoColor,
      promoCopy: promoCopy
    }),
    /*#__PURE__*/
    React.createElement(PromoText, null, promoCopy)))
  );
}

TieredPromo.propTypes = {
  /** Allows extending styles with styled(TieredPromo) constructor. */
  className: PropTypes.string,

  /** Numeric value of cart items. */
  subtotal: PropTypes.number,

  /** Distinguishes between implementations. */
  variant: PropTypes.oneOf(VARIANTS)
};

var addSpan = function addSpan(firstStr, secondStr, nextSavings) {
  return (
    /*#__PURE__*/
    React.createElement(React.Fragment, null, firstStr,
    /*#__PURE__*/
    React.createElement("span", null, nextSavings), secondStr)
  );
}; // ToDo: replace with actual savings and goals.


var savingsInfo = [{
  savings: 150,
  savingsGoal: 1500
}, {
  savings: 350,
  savingsGoal: 3500
}, {
  savings: 750,
  savingsGoal: 7500
}, {
  savings: 1500,
  savingsGoal: 15000
}, {
  savings: 2500,
  savingsGoal: 25000
}]; // ToDo: replace with actual tier color assignments.

var tierColors = [successM, logoBlueM, logoGreenM, logoYellowM, primaryM];
var styles = {
  CongratsMsg: styled.div(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n    display: flex;\n    margin-bottom: 1rem;\n  "]))),
  CongratsMsgText: styled.p(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n    ", ";\n\n    span {\n      ", ";\n    }\n  "])), BodySmallStyles, BodyBoldSmallStyles),
  Container: styled.div(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n    align-items: flex-start;\n    box-sizing: border-box;\n    display: flex;\n    flex-direction: column;\n    width: 100%;\n\n    ", "\n\n    ", "\n  "])), function (p) {
    return p.variant === 'default' && "\n      margin-top: 1rem;\n    ";
  }, function (p) {
    return p.variant === 'checkout' && "\n      background-color: ".concat(backgroundGrayLightM, ";\n      border-radius: 0.25rem;\n      margin-bottom: 0.75rem;\n      padding: 1.5rem;\n    ");
  }),
  ProgressBar: styled.div(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n    // Background color is hex of successM with 50% opacity - unique value.\n    background-color: #a6afb3;\n    border-radius: 0.625rem;\n    height: 0.375rem;\n    margin-bottom: 0.75rem;\n    max-width: 54rem;\n    overflow: hidden;\n    width: 100%;\n\n    ", "\n  "])), function (p) {
    return p.variant === 'checkout' && "\n      margin-bottom: 1rem;\n    ";
  }),
  Progress: styled.div(_templateObject5 || (_templateObject5 = _taggedTemplateLiteral(["\n    background-color: ", ";\n    border-radius: 0.625rem;\n    height: inherit;\n    transition: all 500ms ease-out;\n    width: ", "%;\n  "])), function (p) {
    return p.promoColor;
  }, function (p) {
    return p.value * 100;
  }),
  PromoCheckIcon: styled(CheckIcon)(_templateObject6 || (_templateObject6 = _taggedTemplateLiteral(["\n    margin-left: 0.5rem;\n    min-width: 1.5rem;\n  "]))),
  PromoIconProgress: styled(PromoIcon)(_templateObject7 || (_templateObject7 = _taggedTemplateLiteral(["\n    margin-right: 0.5rem;\n    min-width: 1.5rem;\n\n    ", "\n\n    path {\n      fill: ", ";\n      transition: fill 500ms ease-out;\n    }\n  "])), function (p) {
    return !p.promoCopy && "display: none; ";
  }, function (p) {
    return p.promoColor || successM;
  }),
  PromoTextWrapper: styled.div(_templateObject8 || (_templateObject8 = _taggedTemplateLiteral(["\n    align-items: flex-start;\n    display: flex;\n  "]))),
  PromoText: styled.p(_templateObject9 || (_templateObject9 = _taggedTemplateLiteral(["\n    ", ";\n\n    span {\n      ", ";\n    }\n  "])), BodySmallStyles, BodyBoldSmallStyles)
};
export default TieredPromo;